// escalation-trigger-from-journal.ts
//
// The ONE map from a journaled stop to an `escalations[]` item — `run --json`
// and `status --json` both call it, so the two surfaces cannot drift.
import { readdir, readFile } from "node:fs/promises";
import { join } from "node:path";
import type { StopAttemptRecord } from "./attempt-finished-journal-reader.js";
import type { BlockedBy } from "./escalation-blocked-by.js";
import {
  RECOVERY_SKIP_OP_KIND,
  RECOVERY_TICKET_OP_KIND,
  type RecoveryOpRecord,
  type RecoverySkipReason,
} from "./recovery-guards.js";
import { readRecoveryJournal } from "./recovery-ledger-from-journal.js";
import { type RecoveryTrigger, recoveryTriggerOf } from "./recovery-trigger-classifier.js";

export interface EscalationItem {
  ticket_id: string;
  kind: string;
  trigger: RecoveryTrigger;
  category: string;
  blocked_by: BlockedBy;
  /** Recovery ticket the controller wrote for THIS stop (matched by `trigger_seq`). */
  recovery_ticket?: string;
  /** Why it did not (T1–T3 only; an `other` item never carries it). */
  recovery_skipped_reason?: RecoverySkipReason;
}

function recoveryFieldsFor(
  stop: StopAttemptRecord,
  ledger: readonly RecoveryOpRecord[],
): Pick<EscalationItem, "recovery_ticket" | "recovery_skipped_reason"> {
  const ops = ledger.filter(
    (r) => r.trigger_seq === stop.seq && r.source_ticket === stop.ticket_id,
  );
  const applied = ops.find(
    (r) => r.operation_kind === RECOVERY_TICKET_OP_KIND && r.result === "applied",
  )?.ticket_id;
  if (applied !== undefined) return { recovery_ticket: applied };
  const skip = ops.find(
    (r) => r.operation_kind === RECOVERY_SKIP_OP_KIND && r.result === "skipped",
  );
  return skip?.reason === undefined ? {} : { recovery_skipped_reason: skip.reason };
}

/** Bounds the machine output; the operating table still lists every ticket. */
export const MAX_ESCALATION_ITEMS = 20;

/** A ticket missing from the repo maps to kind `unknown` (never recovery-eligible). */
export function escalationItemsFromStops(
  stops: readonly StopAttemptRecord[],
  kindByTicket: ReadonlyMap<string, string>,
  ledger: readonly RecoveryOpRecord[] = [],
): EscalationItem[] {
  return stops.slice(0, MAX_ESCALATION_ITEMS).map((stop) => {
    const kind = kindByTicket.get(stop.ticket_id) ?? "unknown";
    const trigger = recoveryTriggerOf(kind, stop.blocked_by);
    return {
      ticket_id: stop.ticket_id,
      kind,
      trigger,
      category: stop.category,
      blocked_by: stop.blocked_by,
      ...(trigger === "other" ? {} : recoveryFieldsFor(stop, ledger)),
    };
  });
}

/**
 * id → kind from `<workspace>/.avatar/tickets/*.json`, file by file: one
 * malformed ticket loses only its own kind (not every kind, as a whole-dir
 * schema parse would — a plan would then read as `counted_cap`, not T3).
 */
async function readTicketKinds(missionWorkspace: string): Promise<Map<string, string>> {
  const dir = join(missionWorkspace, ".avatar", "tickets");
  const kinds = new Map<string, string>();
  const files = await readdir(dir).catch(() => [] as string[]);
  for (const file of files.filter((f) => f.endsWith(".json"))) {
    try {
      const { id, kind } = JSON.parse(await readFile(join(dir, file), "utf8")) as Record<
        string,
        unknown
      >;
      if (typeof id === "string" && typeof kind === "string") kinds.set(id, kind);
    } catch {
      // unreadable ticket ⇒ its stop reads kind `unknown` (never recovery-eligible)
    }
  }
  return kinds;
}

/** B19: a stop from an earlier run whose recovery (applied/skipped) was journaled in THIS run. */
function settledAfter(
  stop: StopAttemptRecord,
  ledger: readonly RecoveryOpRecord[],
  afterSeq: number,
): boolean {
  return ledger.some(
    (r) =>
      r.trigger_seq === stop.seq &&
      (r.result === "applied" || r.result === "skipped") &&
      (r.result_seq ?? 0) > afterSeq,
  );
}

/** `status`: tickets still blocked by their last stop · `run`: stops journaled after `afterSeq` (+ B19). */
export type EscalationScope = { standing: true } | { afterSeq: number };

/** Lock-free read (journal + repo tickets for the kind); best-effort, never throws. */
export async function readEscalations(
  controlRoot: string,
  missionWorkspace: string | undefined,
  scope: EscalationScope,
): Promise<EscalationItem[]> {
  const { scan, ledger } = await readRecoveryJournal(controlRoot);
  const afterSeq = "afterSeq" in scope ? scope.afterSeq : undefined;
  const stops =
    afterSeq === undefined
      ? scan.standing
      : scan.stops.filter((s) => s.seq > afterSeq || settledAfter(s, ledger, afterSeq));
  if (stops.length === 0) return [];
  const kinds =
    missionWorkspace === undefined ? new Map() : await readTicketKinds(missionWorkspace);
  return escalationItemsFromStops(stops, kinds, ledger);
}
